import { cx } from '@flowus/common/cx';
import type { BlockDTO, SearchBlockDTO } from '@next-space/fe-api-idl';
import { BlockType, CollectionViewType, SearchDocSortType } from '@next-space/fe-api-idl';
import useDebounceEffect from 'ahooks/lib/useDebounceEffect';
import type { FC } from 'react';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { LoadingContainer } from 'src/common/components/loading-container';
import { segmentsToText } from 'src/editor/utils/editor';
import { getOwnerPage } from 'src/hooks/block/use-get-owner-page';
import { useGlobalSearch } from 'src/hooks/block/use-global-search';
import { cache } from 'src/redux/store';
import { bizTracker } from 'src/utils/biz-tracker';
import { isCollection } from 'src/utils/block-type-utils';
import { getUntitledName } from 'src/utils/get-untitled-name';
import { useGetPageId } from 'src/utils/getPageId';
import { getLocationOrigin } from 'src/utils/location-utils';
import { usePickBlock } from 'src/utils/pick-block';
import { urlFetcher } from 'src/utils/url-fetcher';
import { BlockDefaultIcon } from '../block-default-icon';
import { IconTrigger } from '../icon-trigger';
import type { RecommendPage } from './fetch-recomend-pages';
import { fetchRecommendPages } from './fetch-recomend-pages';
import { useSpaceCapacity } from 'src/services/capacity';
import { Icon } from 'src/common/components/icon';
import { useEnableAI } from 'src/hooks/block/use-enable-AI';
import { setLocalStorage } from '@flowus/common';
import { HighlightPlus } from '@flowus/common/components/hight-light-plus';
import { AiTip } from './ai-tip';

const HELP_CENTER_SPACE_ID = __BUILD_IN__
  ? '2f62c73e-abfc-4c18-b20d-0951c7d79521'
  : 'e5418b54-223f-4f62-933f-cc5da586e910';
const HELP_CENTER_PAGE_ID = __BUILD_IN__
  ? 'ed00d504-90ac-4a3c-9914-2a7edf841d7a'
  : 'e68380be-23cf-42c4-9b95-23a52713213d';

// 由于这个是本地存储的展示逻辑，先设定个大概的时间，后面活动没了也不至于还显示出来
const expireTime = new Date('2023-12-12').getTime();
const isValidTime = Date.now() < expireTime;
const HIDE_TIP_KEY = 'hideTemplateShareTip';
const hideTemplateShareTip = localStorage.getItem(HIDE_TIP_KEY) === 'true';

export const RecommendList: FC<{ className?: string; keyword?: string }> = (props) => {
  const { keyword } = props;
  const pageId = useGetPageId();
  const [recommendItems, setRecommendItems] = useState<RecommendPage[]>([]);
  const [searchResultItems, setSearchResultItems] = useState<
    (SearchBlockDTO & { navs: BlockDTO[] })[]
  >([]);
  const [loading, setLoading] = useState(false);
  const globalSearch = useGlobalSearch();
  const capacity = useSpaceCapacity();
  const { enableAI } = useEnableAI();

  const showTemplateShareTip =
    !__BUILD_IN__ && isValidTime && !hideTemplateShareTip && enableAI && capacity.aiRestUsage < 10;

  useLayoutEffect(() => {
    if (keyword) {
      setLoading(true);
    }
  }, [keyword]);

  useDebounceEffect(
    () => {
      const search = async () => {
        if (!keyword) {
          setLoading(false);
          setSearchResultItems([]);
          return;
        }
        try {
          const { results } = await globalSearch({
            space: HELP_CENTER_SPACE_ID,
            page: 1,
            perPage: 50, // 帮助中心页面数量有限，不需要做加载更多的逻辑
            query: keyword,
            source: 'quickFindPublic',
            sort: SearchDocSortType.RELEVANCE,
            filters: { ancestors: [HELP_CENTER_PAGE_ID] }, // 在帮助中心内搜索
          });
          setSearchResultItems(results);
        } catch (e) {
          setSearchResultItems([]);
        } finally {
          setLoading(false);
        }
      };
      void search();
    },
    [keyword],
    { wait: 800 }
  );

  useEffect(() => {
    if (__PRIVATE__) return;
    const block = cache.blocks[pageId];
    let viewType: CollectionViewType | undefined = undefined;
    if (isCollection(block?.type)) {
      const viewId = location.hash.slice(1).trim();
      if (viewId) {
        viewType = cache.collectionViews[viewId]?.type ?? CollectionViewType.TABLE;
      }
    }
    void fetchRecommendPages({
      blockType: cache.blocks[pageId]?.type ?? BlockType.PAGE,
      viewType,
    }).then((res) => {
      res && setRecommendItems(res);
    });
  }, [pageId]);

  if (keyword) {
    if (loading) {
      return <LoadingContainer style={{ marginTop: '30px', height: '120px' }} />;
    }
    if (searchResultItems) {
      return (
        <div className={cx(props.className)}>
          {searchResultItems.length === 0 && (
            <div className="py-5 text-t2 text-grey4 text-center leading-[22px]">无相关页面</div>
          )}
          <AiTip keyword={keyword} />
          {searchResultItems.map((item, index) => {
            return (
              <SearchResultItem
                key={index}
                keyword={keyword}
                item={item}
                onClick={() => {
                  const page = cache.blocks[item.pageId ?? ''];
                  if (!page) return;
                  let jumpPageId = page.uuid;
                  const ownerPageId = getOwnerPage(item.uuid);
                  // 如果是内嵌就跳到内嵌上去,从全局搜索上抄来的逻辑
                  if (ownerPageId && cache.blocks[ownerPageId]?.type === BlockType.MIND_MAPPING) {
                    jumpPageId = ownerPageId;
                  }
                  bizTracker.event('help_page_clk', {
                    page_id: jumpPageId,
                  });
                  const url = `${getLocationOrigin()}/share/${jumpPageId}${item.uuid}`;
                  window.open(url);
                }}
              />
            );
          })}
        </div>
      );
    }
  }

  return (
    <div className={cx(props.className)}>
      {showTemplateShareTip && <TemplateShareItem />}
      <AiTip keyword={keyword} />
      {recommendItems.map((item) => {
        return <RecommendItem key={item.blockId} item={item} />;
      })}
    </div>
  );
};

export const SearchResultItem: FC<{
  item: SearchBlockDTO;
  keyword: string;
  onClick: () => void;
}> = (props) => {
  const { item, keyword } = props;
  const page = usePickBlock(item.pageId, ['data']);

  const title = useMemo(() => {
    if (!page) return '';
    const title = segmentsToText(page.data.segments);
    if (title) return title;
    return getUntitledName(page.type);
  }, [page]);

  if (!page) return null;

  // 是否匹配到page，是的话就是匹配到title
  const matchPage = item.uuid === item.pageId;
  return (
    <div
      className="mt-2.5 flex items-center h-[70px] w-full border border-grey6 px-2.5 py-4 rounded-md cursor-pointer hover:bg-black_003"
      style={{
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.06)',
      }}
      onClick={props.onClick}
    >
      <div className="text-xl leading-none block">
        <IconTrigger
          blockId={props.item.pageId ?? ''}
          trigger={false}
          defaultIcon={<BlockDefaultIcon uuid={props.item.pageId} />}
        />
      </div>
      <div className="ml-2 text-ellipsis">
        {matchPage ? (
          <HighlightPlus limitLen={16} text={title} search={keyword} className="text-t2-medium" />
        ) : (
          <div className="text-t2-medium text-ellipsis">{title}</div>
        )}
        <HighlightPlus
          limitLen={16}
          text={item.hitText}
          search={keyword}
          className="text-t4"
          containerClassName="text-ellipsis mt-0.5 w-[95%]"
        />
      </div>
    </div>
  );
};

const TemplateShareItem: FC = () => {
  const [showRed, setShowRed] = useState(true);
  return (
    <div
      className="relative mt-2.5 flex items-center h-[70px] w-full border border-grey6 px-2.5 py-4 rounded-md cursor-pointer hover:bg-black_003"
      style={{
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.06)',
      }}
      onClick={() => {
        setShowRed(false);
        setLocalStorage(HIDE_TIP_KEY, 'true');
        // 产品给的地址
        window.open('https://flowus.cn/share/233ede99-28b5-4510-a491-868687670d88');
      }}
    >
      <Icon name={'IcAi'} size="large" />
      <div className="ml-1 w-full text-ellipsis">
        <div className="text-t2-medium">{'🛎 当前空间AI使用次数已经不足10次啦！'}</div>
        <div className="text-t4 text-grey3 mt-0.5 w-[95%]">
          {'现在分享【模板库】模板，至高免费可得20次AI补充额度，了解详情 >>'}
        </div>
      </div>
      {showRed && <div className="absolute left-2 top-2 bg-red w-2 h-2 rounded-full" />}
    </div>
  );
};

const RecommendItem: FC<{ item: RecommendPage }> = (props) => {
  const { icon, blockId, title, desc, url } = props.item;
  const [iconUrl, setIconUrl] = useState('');
  useEffect(() => {
    if (!icon) return;
    if (!iconUrl) return;
    void urlFetcher.fetchImageUrl({ blockId, ossName: icon }).then((res) => {
      setIconUrl(res);
    });
  }, [blockId, icon, iconUrl]);
  return (
    <div
      className="mt-2.5 flex items-center h-[70px] w-full border border-grey6 px-2.5 py-4 rounded-md cursor-pointer hover:bg-black_003"
      style={{
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.06)',
      }}
      onClick={() => {
        const uuid = url.slice(-36);
        bizTracker.event('help_page_clk', {
          page_id: uuid,
        });
        window.open(url);
      }}
    >
      {iconUrl && <img src={iconUrl} className="w-9 h-9 dark:invert dark:opacity-50" />}
      <div className="ml-1 w-full text-ellipsis">
        <div className="text-t2-medium">{title}</div>
        {desc && <div className="text-t4 text-grey3 mt-0.5 w-[95%]">{desc}</div>}
      </div>
    </div>
  );
};
